import React, { useState, useEffect } from "react";
import "./createBookingDialog.css";
import axios from "axios";

export default function CreateBookingDialog({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  initialData = null,
  isEdit = false
}) {
  // Initial form state
  const initialFormState = {
    bookingType: "Domestic",
    clientId: "",
    agentId: "",
    startDate: "",
    endDate: "",
    totalAmount: "0",
    amountPaid: "0",
    amountDue: "0",
    specialRequests: "",
    bookingStatus: "Confirmed",
    paymentStatus: "Pending"
  };

  const [formData, setFormData] = useState(initialFormState);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchClients();
      fetchAgents();
      
      if (initialData) {
        // For edit mode, pre-fill the form
        console.log("Initial data for edit:", initialData);
        
        // Parse dates correctly
        const formatDateForInput = (dateString) => {
          if (!dateString) return "";
          try {
            const date = new Date(dateString);
            // Handle invalid dates
            if (isNaN(date.getTime())) return "";
            return date.toISOString().split('T')[0];
          } catch (error) {
            console.error("Error parsing date:", error);
            return "";
          }
        };

        setFormData({
          bookingType: initialData.bookingType || "Domestic",
          clientId: initialData.clientId?._id || initialData.clientId || "",
          agentId: initialData.agentId?._id || initialData.agentId || "",
          startDate: formatDateForInput(initialData.startDate),
          endDate: formatDateForInput(initialData.endDate),
          totalAmount: initialData.totalAmount?.toString() || "0",
          amountPaid: initialData.amountPaid?.toString() || "0",
          amountDue: initialData.amountDue?.toString() || "0",
          specialRequests: initialData.specialRequests || "",
          bookingStatus: initialData.bookingStatus || "Confirmed",
          paymentStatus: initialData.paymentStatus || "Pending"
        });
      } else {
        // For create mode, reset form
        setFormData(initialFormState);
      }
      setErrors({});
      setApiError(null);
    }
  }, [open, initialData]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      setApiError(null);
      console.log("Fetching clients...");
      
      // Use environment variable if available
      const baseURL = process.env.REACT_APP_API_BASE_URL || "https://crmapi.zauntrip.com";
      const response = await axios.get(`${baseURL}/api/clients`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log("Clients response:", response.data);
      
      if (response.data.success) {
        // Handle different response structures
        const clientsData = response.data.data || response.data.clients || [];
        setClients(Array.isArray(clientsData) ? clientsData : []);
      } else {
        console.error("Failed to fetch clients:", response.data.message);
        setApiError(`Failed to load clients: ${response.data.message}`);
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      setApiError(`Failed to load clients: ${err.message}`);
      
      // For development, use mock clients
      if (process.env.NODE_ENV === 'development') {
        console.log("Using mock clients data");
        setClients([
          { _id: "client1", fullName: "John Doe", email: "john@example.com", phone: "+91 9876543210" },
          { _id: "client2", fullName: "Jane Smith", email: "jane@example.com", phone: "+91 9876543211" }
        ]);
        setApiError(null);
      }
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      console.log("Fetching agents...");
      
      const baseURL = process.env.REACT_APP_API_BASE_URL || "https://crmapi.zauntrip.com";
      const response = await axios.get(`${baseURL}/api/agents`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log("Agents response:", response.data);
      
      if (response.data.success) {
        // Handle different response structures
        const agentsData = response.data.data || response.data.agents || [];
        setAgents(Array.isArray(agentsData) ? agentsData : []);
      } else {
        console.error("Failed to fetch agents:", response.data.message);
      }
    } catch (err) {
      console.error("Error fetching agents:", err);
      
      // For development, use mock agents
      if (process.env.NODE_ENV === 'development') {
        console.log("Using mock agents data");
        setAgents([
          { _id: "agent1", firstName: "Sarah", lastName: "Johnson", email: "sarah@example.com" },
          { _id: "agent2", firstName: "Michael", lastName: "Chen", email: "michael@example.com" }
        ]);
      }
    } finally {
      setLoadingAgents(false);
    }
  };

  if (!open) return null;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.bookingType) newErrors.bookingType = "Booking type is required";
    if (!formData.clientId) newErrors.clientId = "Client selection is required";
    if (!formData.agentId) newErrors.agentId = "Agent selection is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    
    // Validate dates
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (endDate < startDate) {
        newErrors.endDate = "End date cannot be before start date";
      }
    }
    
    // Validate financials
    const totalAmount = parseFloat(formData.totalAmount) || 0;
    const amountPaid = parseFloat(formData.amountPaid) || 0;
    
    if (totalAmount <= 0) newErrors.totalAmount = "Total amount must be greater than 0";
    if (amountPaid < 0) newErrors.amountPaid = "Amount paid cannot be negative";
    if (amountPaid > totalAmount) {
      newErrors.amountPaid = "Amount paid cannot exceed total amount";
    }
    
    // Validate statuses
    const validBookingStatuses = ["Confirmed", "Pending", "Cancelled"];
    const validPaymentStatuses = ["Pending", "Partial", "Paid"];
    
    if (!validBookingStatuses.includes(formData.bookingStatus)) {
      newErrors.bookingStatus = "Invalid booking status";
    }
    
    if (!validPaymentStatuses.includes(formData.paymentStatus)) {
      newErrors.paymentStatus = "Invalid payment status";
    }
    
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    const updatedFormData = {
      ...formData,
      [name]: value
    };

    // Recalculate amount due if totalAmount or amountPaid changes
    if (name === "totalAmount" || name === "amountPaid") {
      const total = parseFloat(updatedFormData.totalAmount) || 0;
      const paid = parseFloat(updatedFormData.amountPaid) || 0;
      updatedFormData.amountDue = (total - paid).toString();
      
      // Auto-update payment status based on amounts
      if (total > 0) {
        if (paid === 0) {
          updatedFormData.paymentStatus = "Pending";
        } else if (paid > 0 && paid < total) {
          updatedFormData.paymentStatus = "Partial";
        } else if (paid >= total) {
          updatedFormData.paymentStatus = "Paid";
        }
      }
    }

    // Handle end date validation
    if (name === "startDate" && formData.endDate) {
      const newStartDate = new Date(value);
      const currentEndDate = new Date(formData.endDate);
      if (currentEndDate < newStartDate) {
        updatedFormData.endDate = value; // Reset end date to start date
      }
    }

    setFormData(updatedFormData);
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Calculate amount due
    const totalAmount = parseFloat(formData.totalAmount) || 0;
    const amountPaid = parseFloat(formData.amountPaid) || 0;
    const amountDue = totalAmount - amountPaid;
    
    // Prepare final data for backend
    const bookingData = {
      ...formData,
      totalAmount: formData.totalAmount,
      amountPaid: formData.amountPaid,
      amountDue: amountDue.toString(),
      clientId: formData.clientId,
      agentId: formData.agentId
    };
    
    // Clean up data - remove empty strings
    if (bookingData.specialRequests === "") {
      delete bookingData.specialRequests;
    }
    
    console.log("Submitting booking data:", bookingData);
    
    setSubmitLoading(true);
    setApiError(null);
    
    try {
      await onSubmit(bookingData);
      // If successful, close the dialog
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
      setApiError(error.message || "Failed to save booking");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClientSelect = (clientId) => {
    const selectedClient = clients.find(c => c._id === clientId);
    if (selectedClient) {
      setFormData({
        ...formData,
        clientId,
        specialRequests: selectedClient.notes || formData.specialRequests
      });
    }
    
    // Clear error
    if (errors.clientId) {
      setErrors({
        ...errors,
        clientId: null
      });
    }
  };

  // Get full name for agent display
  const getAgentFullName = (agent) => {
    if (!agent) return "Unknown Agent";
    if (typeof agent === 'string') return agent;
    return `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || "Unknown Agent";
  };

  // Get full name for client display
  const getClientFullName = (client) => {
    if (!client) return "Unknown Client";
    if (typeof client === 'string') return client;
    return client.fullName || client.email || "Unknown Client";
  };

  // Get display info for client
  const getClientDisplayInfo = (client) => {
    const name = getClientFullName(client);
    const email = client.email ? ` - ${client.email}` : "";
    const phone = client.phone ? ` (${client.phone})` : "";
    return `${name}${email}${phone}`;
  };

  // Get display info for agent
  const getAgentDisplayInfo = (agent) => {
    const name = getAgentFullName(agent);
    const email = agent.email ? ` - ${agent.email}` : "";
    return `${name}${email}`;
  };

  // Calculate today's date for min date restrictions
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <div className="dialog-overlay" onClick={(e) => {
      if (e.target.className === "dialog-overlay") onClose();
    }}>
      <div className="dialog-container">
        <div className="dialog-header">
          <h2>{isEdit ? "Edit Booking" : "Create New Booking"}</h2>
          <button 
            className="close-btn" 
            onClick={onClose}
            disabled={submitLoading}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {apiError && (
          <div className="api-error-message">
            <span className="error-text">{apiError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="dialog-form">
          <div className="form-grid">
            {/* Booking Type */}
            <div className={`form-group ${errors.bookingType ? "error" : ""}`}>
              <label htmlFor="bookingType">Booking Type *</label>
              <select
                id="bookingType"
                name="bookingType"
                value={formData.bookingType}
                onChange={handleChange}
                required
                disabled={submitLoading}
                className={errors.bookingType ? "error-input" : ""}
              >
                <option value="Domestic">Domestic</option>
                <option value="International">International</option>
              </select>
              {errors.bookingType && <span className="error-text">{errors.bookingType}</span>}
            </div>

            {/* Client Selection */}
            <div className={`form-group ${errors.clientId ? "error" : ""}`}>
              <label htmlFor="clientId">Select Client *</label>
              <select
                id="clientId"
                name="clientId"
                value={formData.clientId}
                onChange={(e) => handleClientSelect(e.target.value)}
                required
                disabled={loadingClients || submitLoading}
                className={errors.clientId ? "error-input" : ""}
              >
                <option value="">Select a client...</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {getClientDisplayInfo(client)}
                  </option>
                ))}
              </select>
              {loadingClients && <small className="loading-text">Loading clients...</small>}
              {errors.clientId && <span className="error-text">{errors.clientId}</span>}
              {clients.length === 0 && !loadingClients && !apiError && (
                <small className="warning-text">No clients found. Please add clients first.</small>
              )}
            </div>

            {/* Agent Selection */}
            <div className={`form-group ${errors.agentId ? "error" : ""}`}>
              <label htmlFor="agentId">Assign Agent *</label>
              <select
                id="agentId"
                name="agentId"
                value={formData.agentId}
                onChange={handleChange}
                required
                disabled={loadingAgents || submitLoading}
                className={errors.agentId ? "error-input" : ""}
              >
                <option value="">Select an agent...</option>
                {agents.map((agent) => (
                  <option key={agent._id} value={agent._id}>
                    {getAgentDisplayInfo(agent)}
                  </option>
                ))}
              </select>
              {loadingAgents && <small className="loading-text">Loading agents...</small>}
              {errors.agentId && <span className="error-text">{errors.agentId}</span>}
              {agents.length === 0 && !loadingAgents && (
                <small className="warning-text">No agents found. Please add agents first.</small>
              )}
            </div>

            {/* Travel Dates */}
            <div className={`form-group ${errors.startDate ? "error" : ""}`}>
              <label htmlFor="startDate">Start Date *</label>
              <input
                id="startDate"
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                min={getTodayDate()}
                required
                disabled={submitLoading}
                className={errors.startDate ? "error-input" : ""}
              />
              {errors.startDate && <span className="error-text">{errors.startDate}</span>}
            </div>

            <div className={`form-group ${errors.endDate ? "error" : ""}`}>
              <label htmlFor="endDate">End Date</label>
              <input
                id="endDate"
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                min={formData.startDate || getTodayDate()}
                disabled={submitLoading || !formData.startDate}
                className={errors.endDate ? "error-input" : ""}
              />
              {errors.endDate && <span className="error-text">{errors.endDate}</span>}
            </div>

            {/* Financials */}
            <div className={`form-group ${errors.totalAmount ? "error" : ""}`}>
              <label htmlFor="totalAmount">Total Amount (₹) *</label>
              <input
                id="totalAmount"
                type="number"
                min="0"
                step="0.01"
                name="totalAmount"
                value={formData.totalAmount}
                onChange={handleChange}
                required
                disabled={submitLoading}
                className={errors.totalAmount ? "error-input" : ""}
              />
              {errors.totalAmount && <span className="error-text">{errors.totalAmount}</span>}
            </div>

            <div className={`form-group ${errors.amountPaid ? "error" : ""}`}>
              <label htmlFor="amountPaid">Amount Paid (₹)</label>
              <input
                id="amountPaid"
                type="number"
                min="0"
                step="0.01"
                name="amountPaid"
                value={formData.amountPaid}
                onChange={handleChange}
                max={formData.totalAmount}
                disabled={submitLoading}
                className={errors.amountPaid ? "error-input" : ""}
              />
              <div className="amount-due-display">
                <small className={parseFloat(formData.amountDue) > 0 ? "warning-text" : "success-text"}>
                  Amount Due: ₹{(parseFloat(formData.amountDue) || 0).toLocaleString('en-IN')}
                </small>
              </div>
              {errors.amountPaid && <span className="error-text">{errors.amountPaid}</span>}
            </div>

            {/* Status */}
            <div className={`form-group ${errors.bookingStatus ? "error" : ""}`}>
              <label htmlFor="bookingStatus">Booking Status</label>
              <select
                id="bookingStatus"
                name="bookingStatus"
                value={formData.bookingStatus}
                onChange={handleChange}
                disabled={submitLoading}
                className={errors.bookingStatus ? "error-input" : ""}
              >
                <option value="Confirmed">Confirmed</option>
                <option value="Pending">Pending</option>
                <option value="Cancelled">Cancelled</option>
              </select>
              {errors.bookingStatus && <span className="error-text">{errors.bookingStatus}</span>}
            </div>

            <div className={`form-group ${errors.paymentStatus ? "error" : ""}`}>
              <label htmlFor="paymentStatus">Payment Status</label>
              <select
                id="paymentStatus"
                name="paymentStatus"
                value={formData.paymentStatus}
                onChange={handleChange}
                disabled={submitLoading}
                className={errors.paymentStatus ? "error-input" : ""}
              >
                <option value="Pending">Pending</option>
                <option value="Partial">Partial</option>
                <option value="Paid">Paid</option>
              </select>
              {errors.paymentStatus && <span className="error-text">{errors.paymentStatus}</span>}
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="specialRequests">Special Requests</label>
            <textarea
              id="specialRequests"
              name="specialRequests"
              rows="3"
              value={formData.specialRequests}
              onChange={handleChange}
              placeholder="Any special requirements, dietary restrictions, room preferences, etc..."
              disabled={submitLoading}
              maxLength="500"
            />
            <small className="character-count">
              {formData.specialRequests.length}/500 characters
            </small>
          </div>

          <div className="dialog-actions">
            <button 
              type="button" 
              className="btn-outline" 
              onClick={onClose}
              disabled={submitLoading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={isLoading || submitLoading || loadingClients || loadingAgents}
            >
              {submitLoading ? (
                <>
                  <span className="spinner"></span>
                  Saving...
                </>
              ) : isEdit ? "Update Booking" : "Create Booking"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}